export const SETTINGS_STORAGE_KEY = 'kinetic-settings';

export const DEFAULT_SETTINGS = {
  transferLimits: {
    perFileSizeLimitMb: 0,
    ramBufferLimitMb: 2
  },
  bandwidthLimits: {
    uploadRateLimitKb: 0,
    downloadRateLimitKb: 0
  },
  timeouts: {
    connectionSec: 20,
    idleSec: 0,
    connectionTimeoutMs: 20000,
    transferTimeoutMs: 30000
  },
  transferMode: {
    overwriteRule: 'prompt'
  },
  uiux: {
    defaultLocalPath: '',
    defaultRemotePath: '',
    showHiddenFiles: true,
    confirmDelete: true,
    dragDropBehavior: 'upload'
  },
  queueBehavior: {
    pauseOnError: true
  },
  logging: {
    level: 'info',
    retention: 1000,
    exportLogs: false
  },
  compression: {
    enableSftpCompression: false,
    zipBeforeTransfer: false
  }
};

export const normalizeBoolean = (value, fallback) =>
typeof value === 'boolean' ? value : fallback;

export const normalizeString = (value, fallback = '') =>
typeof value === 'string' ? value : fallback;

export const normalizeEnum = (value, allowed, fallback) =>
allowed.includes(value) ? value : fallback;

export const normalizeInt = (value, fallback, min = 0, max = 9999) => {
  const n = parseInt(value, 10);
  if (isNaN(n)) return fallback;
  return Math.max(min, Math.min(max, n));
};

export const normalizeFloat = (value, fallback, min = 0, max = 9999) => {
  const n = parseFloat(value);
  if (isNaN(n)) return fallback;
  return Math.max(min, Math.min(max, n));
};

export const normalizeSettings = (value) => {
  if (!value || typeof value !== 'object') return { ...DEFAULT_SETTINGS };
  const s = { ...DEFAULT_SETTINGS };


  if (value.transferLimits) {
    s.transferLimits = {
      ...s.transferLimits,
      perFileSizeLimitMb: normalizeInt(
        value.transferLimits.perFileSizeLimitMb,
        0,
        0,
        100000
      ),
      ramBufferLimitMb: normalizeInt(
        value.transferLimits.ramBufferLimitMb,
        DEFAULT_SETTINGS.transferLimits.ramBufferLimitMb,
        0,
        128
      )
    };
  }


  if (value.bandwidthLimits) {
    s.bandwidthLimits = {
      ...s.bandwidthLimits,
      uploadRateLimitKb: normalizeInt(
        value.bandwidthLimits.uploadRateLimitKb,
        0,
        0,
        999999
      ),
      downloadRateLimitKb: normalizeInt(
        value.bandwidthLimits.downloadRateLimitKb,
        0,
        0,
        999999
      )
    };
  }


  if (value.timeouts) {
    s.timeouts = {
      ...s.timeouts,
      connectionSec: normalizeInt(
        value.timeouts.connectionSec,
        DEFAULT_SETTINGS.timeouts.connectionSec,
        5,
        600
      ),
      idleSec: normalizeInt(
        value.timeouts.idleSec,
        DEFAULT_SETTINGS.timeouts.idleSec,
        0,
        3600
      ),
      connectionTimeoutMs: normalizeInt(
        value.timeouts.connectionTimeoutMs,
        DEFAULT_SETTINGS.timeouts.connectionTimeoutMs,
        1000,
        300000
      ),
      transferTimeoutMs: normalizeInt(
        value.timeouts.transferTimeoutMs,
        DEFAULT_SETTINGS.timeouts.transferTimeoutMs,
        1000,
        300000
      )
    };
  }


  if (value.transferMode) {
    s.transferMode = {
      ...s.transferMode,
      overwriteRule: normalizeEnum(
        value.transferMode.overwriteRule,
        ['prompt', 'skip', 'replace', 'rename'],
        'prompt'
      )
    };
  }


  if (value.uiux) {
    s.uiux = {
      ...s.uiux,
      defaultLocalPath: normalizeString(value.uiux.defaultLocalPath, ''),
      defaultRemotePath: normalizeString(value.uiux.defaultRemotePath, ''),
      showHiddenFiles: normalizeBoolean(
        value.uiux.showHiddenFiles,
        DEFAULT_SETTINGS.uiux.showHiddenFiles
      ),
      confirmDelete: normalizeBoolean(
        value.uiux.confirmDelete,
        DEFAULT_SETTINGS.uiux.confirmDelete
      ),
      dragDropBehavior: normalizeEnum(
        value.uiux.dragDropBehavior,
        ['upload', 'confirm', 'disabled'],
        'upload'
      )
    };
  }


  if (value.logging) {
    s.logging = {
      ...s.logging,
      level: normalizeEnum(
        value.logging.level,
        ['debug', 'info', 'warn', 'error'],
        'info'
      ),
      retention: normalizeInt(value.logging.retention, 1000, 10, 10000),
      exportLogs: normalizeBoolean(value.logging.exportLogs, false)
    };
  }


  if (value.queueBehavior) {
    s.queueBehavior = {
      ...s.queueBehavior,
      pauseOnError: normalizeBoolean(
        value.queueBehavior.pauseOnError,
        DEFAULT_SETTINGS.queueBehavior.pauseOnError
      )
    };
  }


  if (value.compression) {
    s.compression = {
      ...s.compression,
      enableSftpCompression: normalizeBoolean(
        value.compression.enableSftpCompression,
        false
      ),
      zipBeforeTransfer: normalizeBoolean(
        value.compression.zipBeforeTransfer,
        false
      )
    };
  }

  return s;
};

export const loadSettings = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return normalizeSettings(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
};

export const saveSettings = (settings) => {
  localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
};
